import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';

const { FiX, FiSend, FiFileText, FiUser, FiAlertCircle } = FiIcons;

const EvidenceRequestModal = ({ report, onCancel, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    target_role: 'whistleblower',
    title: '',
    description: '',
    due_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('evidence_requests')
        .insert([{
          report_id: report.id,
          ...formData
        }])
        .select();

      if (error) throw error;

      // Add to report history
      const historyUpdate = [
        ...(report.history || []),
        {
          date: new Date().toISOString(),
          action: `Bevisforespørsel sendt til ${formData.target_role === 'whistleblower' ? 'varsler' : 'omvarslet'}`,
          user: 'System'
        }
      ];

      await supabase.from('reports').update({ history: historyUpdate }).eq('id', report.id);

      onSuccess();
    } catch (err) {
      console.error("Error creating evidence request:", err);
      alert("Kunne ikke sende forespørsel.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-[32px] shadow-2xl overflow-hidden border dark:border-gray-700"
      >
        <div className="p-8 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-lg">
              <SafeIcon icon={FiFileText} />
            </div>
            <div>
              <h2 className="text-sm font-black uppercase tracking-widest dark:text-white">Ny Bevisforespørsel</h2>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">Sak: {report.id}</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Mottaker</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, target_role: 'whistleblower' })}
                className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all font-black text-[10px] uppercase tracking-widest ${
                  formData.target_role === 'whistleblower' 
                  ? 'border-blue-600 bg-blue-50 text-blue-700' 
                  : 'border-gray-100 bg-gray-50 text-gray-400'
                }`}
              >
                <SafeIcon icon={FiUser} /> Varsler
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, target_role: 'reported_party' })}
                className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all font-black text-[10px] uppercase tracking-widest ${
                  formData.target_role === 'reported_party' 
                  ? 'border-purple-600 bg-purple-50 text-purple-700' 
                  : 'border-gray-100 bg-gray-50 text-gray-400'
                }`}
              >
                <SafeIcon icon={FiUser} /> Omvarslet
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Hva etterspørres? (Kort tittel)</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-2xl focus:border-blue-500 outline-none transition-all text-sm font-bold"
              placeholder="F.eks. Timelister for uke 42"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Beskrivelse / Instruksjoner</label>
            <textarea
              required
              rows={4}
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-2xl focus:border-blue-500 outline-none transition-all text-sm font-bold"
              placeholder="Beskriv nøyaktig hva som trengs for å opplyse saken..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest text-gray-500 hover:bg-gray-100 transition-all"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-900/20 flex items-center gap-2 hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {loading ? 'Sender...' : <><SafeIcon icon={FiSend} /> Send forespørsel</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EvidenceRequestModal;